import { ExtractDataType } from '@vfde-sails/utils';
import {
    BTX_TARIFF_CHANGE,
    BTX_VVL,
    CUSTOMER_TYPE_CONSUMER,
    CUSTOMER_TYPE_FAF,
    CUSTOMER_TYPE_GIGAKOMBI,
    CUSTOMER_TYPE_SIMONLY,
    CUSTOMER_TYPE_SOHO,
    CUSTOMER_TYPE_YOUNG,
    ErrorCodes,
    MEMBERTYPE_MEMBER,
} from '../constant';
import {
    DecideEligibility,
    IAPIResponseECRedplus,
    IAPIResponseECUltraCard,
    InlifeEligibility,
    VVLEligibility,
} from '../interfaces/api.interface';
import { EligibilityResult } from '../interfaces/main.interface';

/**
 * Use the provided data to identify if the customer is eligible for stone
 */
export const isEligibleForMulticardContract = (userData: ExtractDataType<IAPIResponseECUltraCard>): EligibilityResult => {
    const { eligible, vacantslots } = userData;

    // Default result - Assuming eligibility.
    const result: EligibilityResult = {
        eligible: true,
    };

    if (!eligible) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotMulticardEligible;
    }

    // Checking non-eligibility conditions
    if (vacantslots === 0) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotEnoughVacatSlotsMulticard;
    }

    return result;
};

/**
 * Use the provided data to identify if the customer is eligible for redplus
 */
export const isEligibleForRedplus = (userData: ExtractDataType<IAPIResponseECRedplus>): EligibilityResult => {
    const { membertype, eligible, vacantslots } = userData;

    // Default result - Assuming eligibility.
    const result: EligibilityResult = {
        eligible: true,
    };

    // Checking non-eligibility conditions
    if (vacantslots === 0) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotEnoughVacatSlotsRedplus;
    }

    if (membertype === MEMBERTYPE_MEMBER || !eligible) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotRedplusEligible;
    }

    return result;
};

/**
 * Decide case
 */
export const isVVLorTariffChange = (userData: DecideEligibility) => {
    const { eligible, prolongationeligible } = userData.inlifeData;

    if (!eligible && prolongationeligible) {
        return isEligibleForVVL(userData);
    }

    return isEligibleForInlife(userData);
};

/**
 * Use the provided data to identify if the customer is eligible for inlife
 */
export const isEligibleForInlife = (userData: InlifeEligibility) => {
    const {
        inlifeData: { eligible, allowedtariffs },
        subscriptionDetailsData: { currenttariff },
        redplusData,
        customerType,
    } = userData;
    const notAllowedRedKias = /GM\d+UL\w*/g;
    const notAllowdSohoOrFFKias = /GM\d+L\w*/g;
    const unavailableTariffForSohoAndFF = (customerType === CUSTOMER_TYPE_SOHO || customerType === CUSTOMER_TYPE_FAF) &&
        currenttariff.kiascode.match(notAllowdSohoOrFFKias);
    const unavailableTariffForInlife = currenttariff.kiascode.match(notAllowedRedKias);

    // Default result - Assuming eligibility.
    const result: EligibilityResult = {
        eligible: true,
        endpointData: {
            btx: BTX_TARIFF_CHANGE,
            customerType,
        },
    };

    // is owner?
    if (redplusData.membertype === MEMBERTYPE_MEMBER) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotOwner;

        return result;
    }

    if (!eligible || allowedtariffs.length < 1) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotInlifeEligible;
    }

    // highest tariff already

    if (eligible && (unavailableTariffForSohoAndFF || unavailableTariffForInlife)) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotAllowedTariff;
    }

    return result;
};

/**
 * Use the provided data to identify if the customer is eligible for vvl
 */
export const isEligibleForVVL = (userData: VVLEligibility): EligibilityResult => {
    const {
        contractProlongationData: {
            standardeligible,
            simonlyvvlallowed,
            prematureprolongationeligible,
            allowedtariffs: contractProlongationAllowedTariffs,
        },
        gigaKombiData: {
            hasgigakombiinternetphonediscount,
            hasgigakombitvdiscount,
            hasgigakombibr5,
        },
        redplusData,
        customerType,
    } = userData;
    // Check for GigaKombi
    const isGigakombi = hasgigakombiinternetphonediscount || hasgigakombitvdiscount || hasgigakombibr5;

    // Default result - Assuming eligibility.
    const result: EligibilityResult = {
        eligible: true,
        endpointData: {
            btx: BTX_VVL,
            customerType,
        },
    };

    // is owner?
    if (redplusData.membertype === MEMBERTYPE_MEMBER) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotOwner;

        return result;
    }

    // Not eligible scenarios
    if ((!standardeligible && !prematureprolongationeligible) || contractProlongationAllowedTariffs.length < 1) {
        result.eligible = false;
        result.errorCode = ErrorCodes.NotVVLEligible;

        // Exit early if not eligible.
        return result;
    }

    // Check for Simonly customer type
    const isSimonly =
        !isGigakombi && simonlyvvlallowed &&
        (customerType === CUSTOMER_TYPE_CONSUMER || customerType === CUSTOMER_TYPE_YOUNG);

    if (isSimonly) {
        result.endpointData!.customerType = CUSTOMER_TYPE_SIMONLY;
    }
    else if (isGigakombi) {
        result.endpointData!.customerType = CUSTOMER_TYPE_GIGAKOMBI;
    }

    return result;
};
